import React from "react";
import { Rocket, Battery, ShieldCheck, Brain, Users, Award } from "lucide-react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";
import Seo from "../../components/Seo";

// Fill in with the founder's real details to publish the founder block on /about.
// Shape: { name, role, photo: "/founder.jpg", why: "first-person story", link, linkLabel }
const FOUNDER = null;

const sections = [
  { id: "mission", label: "Our mission" },
  { id: "why", label: "Why EVIQ exists" },
  { id: "analytics", label: "Battery health analytics" },
  { id: "tesla", label: "Our relationship with Tesla" },
  { id: "principles", label: "How we operate" },
  { id: "team", label: "The team" },
];

export default function About() {
  return (
    <>
    <Seo
      title="About EVIQ — independent EV battery intelligence"
      description="EVIQ is an independent battery-intelligence platform for Tesla, Ford, and Volvo/Polestar owners. Science-grounded SoH analytics, warranty tracking, and verifiable Battery Passports — read-only, owner-first."
      canonical="https://eviq.pro/about"
    />
    <LegalPageLayout
      eyebrow="Company · About"
      title="About EVIQ"
      updated="February 20, 2026"
      intro="EVIQ is an independent battery-intelligence platform built for the millions of EV owners whose vehicles quietly lose health every year — and who deserve to see it, understand it, and act on it before their warranty runs out."
      sections={sections}
    >
      <LegalSection id="mission" title="1. Our mission">
        <p>
          Give every EV owner an honest, science-grounded view of the single most
          expensive component in their vehicle — the battery — so they can charge
          smarter, drive further, sell higher, and claim warranties earlier.
        </p>
      </LegalSection>

      <LegalSection id="why" title="2. Why EVIQ exists">
        <p>
          Modern electric vehicles carry the equivalent of a $20,000 battery pack whose
          health silently degrades over years. Owners are given a range estimate on the
          dashboard, but not the underlying diagnostics that would tell them whether
          today&rsquo;s state-of-charge is normal for their vehicle&rsquo;s age, climate,
          and driving pattern — or a red flag that will one day trigger a warranty claim.
        </p>
        <p>
          Manufacturers understandably keep that intelligence proprietary. EVIQ closes
          the gap by combining the data an owner has already <em>authorized</em> the
          manufacturer to share (via the official Tesla Fleet API), peer-reviewed
          Li-ion aging science, and modern AI narrative, into a single dashboard the
          owner controls.
        </p>
      </LegalSection>

      <LegalSection id="analytics" title="3. Battery health analytics — the EVIQ Score™">
        <p>The EVIQ Score™ is a proprietary 0–100 composite of four sub-scores:</p>
        <ul>
          <li><strong>State-of-Health</strong> — usable capacity vs new EPA capacity.</li>
          <li><strong>Degradation trajectory</strong> — 30-day slope adjusted for temperature and cycle count.</li>
          <li><strong>Anomaly load</strong> — presence of five classes of Tesla battery-management &ldquo;blind-spot&rdquo; patterns (phantom drain, cell imbalance, thermal excursions, calibration drift, precondition inefficiency).</li>
          <li><strong>Warranty margin</strong> — climate-adjusted ETA to the 70% capacity threshold that triggers Tesla&rsquo;s battery warranty replacement.</li>
        </ul>
        <p>
          Every number in the app is traceable back to a measurable signal from the
          Tesla Fleet API. Nothing is invented. Nothing is guessed. And when there
          isn&rsquo;t enough data to compute a value confidently, we say so.
        </p>
      </LegalSection>

      <LegalSection id="tesla" title="4. Our relationship with Tesla">
        <p>
          <strong>EVIQ is an independent application that utilizes the Tesla Fleet API
          after owner authorization.</strong> We are not affiliated with, endorsed by, or
          sponsored by Tesla, Inc.
        </p>
        <p>
          Tesla®, Model S®, Model 3®, Model X®, Model Y®, Cybertruck®, and related marks
          are trademarks of Tesla, Inc. EVIQ uses these marks nominatively to describe
          the vehicles our service supports — not to imply any partnership.
        </p>
        <p>
          When you connect your Tesla to EVIQ, you authenticate directly with Tesla using
          Tesla OAuth. EVIQ receives only the read-only vehicle scopes you approve. See
          our <a href="/security">Security</a> and <a href="/privacy">Privacy</a> pages
          for the specifics.
        </p>
      </LegalSection>

      <LegalSection id="principles" title="5. How we operate">
        <ul>
          <li>
            <strong>Owner-first.</strong> Your telemetry is your telemetry. We build the
            product to answer <em>your</em> questions, not to sell you something.
          </li>
          <li>
            <strong>Read-only, always.</strong> EVIQ never sends commands to your car.
            No unlock, no drive, no honk, no summon.
          </li>
          <li>
            <strong>No data sales, ever.</strong> We don&rsquo;t sell your data. Not to
            insurers, not to advertisers, not to anyone.
          </li>
          <li>
            <strong>Explain the math.</strong> Every score in the app links back to the
            raw signal that produced it, so you can audit us.
          </li>
          <li>
            <strong>Ship weekly.</strong> Small, well-tested improvements every week —
            not annual mega-releases.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="team" title="6. The team">
        <p>
          EVIQ is a small, independent team of engineers, EV owners, and battery
          scientists based in the U.S. If you&rsquo;d like to say hi, reach us at{" "}
          <a href="mailto:support@eviq.pro">support@eviq.pro</a> — a real human reads
          every message.
        </p>
        {/* Founder block — renders once FOUNDER is filled in with real details
            (photo, name, first-person "why I built this", LinkedIn/X link). */}
        {FOUNDER && (
          <div className="not-prose mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex gap-4 items-start" data-testid="founder-block">
            <img src={FOUNDER.photo} alt={FOUNDER.name} className="w-16 h-16 rounded-full object-cover border border-white/15" />
            <div>
              <div className="text-sm text-white font-semibold">{FOUNDER.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{FOUNDER.role}</div>
              <p className="text-[13px] text-zinc-400 leading-relaxed mt-2">{FOUNDER.why}</p>
              <a href={FOUNDER.link} target="_blank" rel="noreferrer" className="text-[12px] text-[#39FF14] hover:underline mt-1 inline-block">
                {FOUNDER.linkLabel}
              </a>
            </div>
          </div>
        )}
      </LegalSection>

      {/* Value cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        <ValueCard icon={Battery} title="Battery-first" body="Every model, every scoring function is built around lithium-ion aging science — not adapted from generic fleet dashboards." />
        <ValueCard icon={Brain} title="AI, transparently" body="AI narrative is a translation layer over deterministic math. You always see the numbers underneath." />
        <ValueCard icon={ShieldCheck} title="Owner control" body="Revoke Tesla access with one click. Delete your account with one email. No dark patterns, ever." />
        <ValueCard icon={Rocket} title="Independent" body="No manufacturer paycheck. No advertiser paycheck. We work for the owners who subscribe." />
        <ValueCard icon={Users} title="Community-shaped" body="Every subscriber gets a direct line to us. Your feedback ships in the next weekly release." />
        <ValueCard icon={Award} title="Science-anchored" body="Every metric traces to a peer-reviewed source, an OEM specification, or a signal from your car." />
      </div>
    </LegalPageLayout>
    </>
  );
}

const ValueCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] hover:border-white/20 transition-colors">
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 border border-white/10"
      style={{ background: "rgba(57,255,20,0.08)" }}
    >
      <Icon className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2} />
    </div>
    <h3 className="text-white font-semibold text-base mb-2">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </div>
);
